// Token readiness for the historic tickets grid. Kept free of React and path aliases so Node can
// load it in the checks. getToken() returns '' when it cannot get a token, so an empty string is
// not a usable token.

export const isUsableToken = (token: unknown): token is string => typeof token === 'string' && token.length > 0

/**
 * Holds the CURRENT token from the existing token hook, exactly as given: no fallback to an older
 * value and no caching. The page sets it during render, so requests always use the latest value
 * and the grid is shown only while that value is usable (fail closed).
 */
export type CurrentToken = ReturnType<typeof createCurrentToken>

export const createCurrentToken = () => {
  let value: unknown
  return {
    set(token: unknown) {
      value = token
    },
    /** The value requests send; may be empty or missing, in which case the grid is not shown. */
    current(): string | undefined {
      return typeof value === 'string' ? value : undefined
    },
    isReady(): boolean {
      return isUsableToken(value)
    },
  }
}

export const MISSING_TOKEN_MESSAGE = 'Token di accesso non disponibile: richiesta non inviata.'

/**
 * Wraps the existing request configuration (passed in unchanged) for the grid's data requests.
 * At send time it checks the CURRENT token and, if it is not usable, throws before the request is
 * configured, so nothing is sent with an empty or missing bearer. DevExtreme turns the throw into
 * a failed load. No retry, cache or fallback.
 */
export const createGuardedBeforeSend =
  <TRequest>(
    token: CurrentToken,
    configure: (bearer: string, timeoutMs: number) => (request: TRequest) => TRequest,
    timeout: number,
  ) =>
  (request: TRequest): TRequest => {
    const current = token.current()
    if (!isUsableToken(current)) throw new Error(MISSING_TOKEN_MESSAGE)
    return configure(current, timeout)(request)
  }
